import { useEffect, useRef, useCallback } from "react";

const DEFAULT_GRID_SIZE = 56;
const DEFAULT_HIGHLIGHT_COLOR = "184, 133, 245"; // light purple (glow peak)
const DEFAULT_RADIUS = 220;

// smooth ease-out falloff: strong near the cursor, gone before the edge of the radius
const falloff = (t) => Math.pow(1 - t, 2.2);

export default function CursorGrid({
  gridSize = DEFAULT_GRID_SIZE,
  highlightColor = DEFAULT_HIGHLIGHT_COLOR,
  radius = DEFAULT_RADIUS,
  disableAnimations = false,
  className = "",
  intensity = 1,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const pulsesRef = useRef([]);
  const rafRef = useRef(null);
  const dimsRef = useRef({ width: 0, height: 0, dpr: 1 });

  const resize = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { width, height } = container.getBoundingClientRect();
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    dimsRef.current = { width, height, dpr };
  }, []);

  useEffect(() => {
    if (disableAnimations) return;
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const ctx = canvas.getContext("2d");

    resize();
    window.addEventListener("resize", resize);

    const isInside = (clientX, clientY) => {
      const rect = container.getBoundingClientRect();
      return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    };

    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current = isInside(e.clientX, e.clientY)
        ? { x: e.clientX - rect.left, y: e.clientY - rect.top }
        : { x: -9999, y: -9999 };
    };

    const handleClick = (e) => {
      if (!isInside(e.clientX, e.clientY)) return;
      const rect = container.getBoundingClientRect();
      pulsesRef.current.push({ x: e.clientX - rect.left, y: e.clientY - rect.top, t: 0 });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("click", handleClick);

    const draw = () => {
      const { width, height, dpr } = dimsRef.current;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const cols = Math.ceil(width / gridSize);
      const rows = Math.ceil(height / gridSize);
      const offsetX = (width - cols * gridSize) / 2;
      const offsetY = (height - rows * gridSize) / 2;
      const { x: mx, y: my } = mouseRef.current;
      const maxPulseRadius = Math.max(width, height) * 0.7;

      pulsesRef.current.forEach((p) => {
        p.t += 0.014;
      });
      pulsesRef.current = pulsesRef.current.filter((p) => p.t < 1);

      // highlighted cells near the cursor / click pulses
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const cx = offsetX + i * gridSize + gridSize / 2;
          const cy = offsetY + j * gridSize + gridSize / 2;
          const dist = Math.hypot(cx - mx, cy - my);

          let glow = dist < radius ? falloff(dist / radius) : 0;

          pulsesRef.current.forEach((p) => {
            const pd = Math.hypot(cx - p.x, cy - p.y);
            const ringRadius = p.t * maxPulseRadius;
            const band = Math.abs(pd - ringRadius);
            if (band < gridSize) {
              const ringGlow = (1 - band / gridSize) * (1 - p.t);
              glow = Math.max(glow, ringGlow);
            }
          });

          glow *= intensity;

          if (glow < 0.03) continue;

          const rx = offsetX + i * gridSize;
          const ry = offsetY + j * gridSize;

          const borderOpacity = 0.15 + glow * 0.75;
          const fillOpacity = glow * 0.12;

          if (glow > 0.4) {
            ctx.shadowBlur = glow * 22;
            ctx.shadowColor = `rgba(${highlightColor}, ${Math.min(glow, 0.7)})`;
          } else {
            ctx.shadowBlur = 0;
          }

          ctx.fillStyle = `rgba(${highlightColor}, ${fillOpacity})`;
          ctx.fillRect(rx, ry, gridSize, gridSize);

          ctx.strokeStyle = `rgba(${highlightColor}, ${borderOpacity})`;
          ctx.lineWidth = 1.25;
          ctx.strokeRect(rx + 0.5, ry + 0.5, gridSize - 1, gridSize - 1);
        }
      }
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", resize);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("click", handleClick);
      cancelAnimationFrame(rafRef.current);
    };
  }, [disableAnimations, gridSize, highlightColor, radius, resize, intensity]);

  if (disableAnimations) return null;

  return (
    <div ref={containerRef} className={`absolute inset-0 pointer-events-none ${className}`}>
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
}
